import { DestroyRef, inject, Injectable, signal, WritableSignal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateService } from '@ngx-translate/core';
import { auditTime, bufferTime, filter, Subject, takeUntil } from 'rxjs';
import { NotificationLevel } from '../enums/NotificationLevel';
import { PairingStatus } from '../enums/PairingStatus';
import { TherapyJobStatus } from '../enums/TherapyJobStatus';
import { TherapyJobV2 } from '../models/TherapyJobV2';
import {
    WsEventAborted,
    WsEventCreation,
    WsEventDripChamberDetected,
    WsEventFinalized,
    WsEventInfusion,
    WsEventMessageKey,
    WsEventPairing,
    WsEventState,
    WsEventType
} from '../models/ws-event';
import { ToastService, ToastType } from '../services/toast.service';
import { WsEventsService } from '../services/websocket.service';
import { TherapyJobStore } from '../store/job.store';
import { NotificationsStore } from '../store/notification.store';

export type WsEvent =
  | WsEventInfusion
  | WsEventPairing
  | WsEventDripChamberDetected
  | WsEventFinalized
  | WsEventAborted
  | WsEventCreation
  | WsEventState;

export const COLLECTOR_BUFFER_TIME = 10_000;
export const PAIRING_SIGNAL_DURATION = 1_000;
/** A resync usually reaches every client at once, so reloads are spread over this window. */
export const RESYNC_JITTER_MAX_TIME = 2_000;

@Injectable({
  providedIn: 'root'
})
export class EventManager {
  private readonly jobStore = inject(TherapyJobStore);
  private readonly notificationStore = inject(NotificationsStore);

  private readonly ws: WsEventsService = inject(WsEventsService);
  private readonly toastService: ToastService = inject(ToastService);
  private readonly translateService: TranslateService = inject(TranslateService);

  private readonly pairing: WritableSignal<WsEventPairing> = signal(null);
  public pairingSignal = this.pairing.asReadonly();
  private timer: ReturnType<typeof setTimeout>;

  private readonly destroyRef = inject(DestroyRef);
  private readonly completeSubscription = new Subject<void>();

  private readonly updatesCollector: Subject<Partial<TherapyJobV2>> = new Subject();
  private readonly notificationRefreshRequests: Subject<void> = new Subject();

  private subscribeToCollector() {
    this.updatesCollector
      .pipe(
        bufferTime(COLLECTOR_BUFFER_TIME),
        filter((events) => !!events?.length),
        takeUntil(this.completeSubscription)
      )
      .subscribe((events: Array<Partial<TherapyJobV2>>) => {
        this.jobStore.applyWsUpdates(events);
      });

    // Unlike jobStore.load these refreshes are not debounced, so a replayed batch would otherwise
    // fire one request per event.
    this.notificationRefreshRequests.pipe(auditTime(0), takeUntil(this.completeSubscription)).subscribe(() => {
      this.notificationStore.init(this.jobStore.entities()).subscribe();
    });
  }

  public initSubscription() {
    this.subscribeToCollector();

    this.ws
      .events()
      .pipe(takeUntilDestroyed(this.destroyRef), takeUntil(this.completeSubscription))
      .subscribe((wsEvent: WsEvent) => {
        if (!wsEvent) {
          return;
        }

        if (wsEvent.type === WsEventType.RESYNC_REQUIRED) {
          this.handleResyncRequired();
          return;
        }

        if (wsEvent.type === WsEventType.JOB_UPDATE) {
          this.handleInfusionUpdate(wsEvent as WsEventInfusion);
          return;
        }

        if (wsEvent.type === WsEventType.JOB_FINISH) {
          this.handleJobFinish(wsEvent as WsEventInfusion);
          return;
        }

        if (wsEvent.type === WsEventType.PAIRING) {
          this.handlePairing(wsEvent as WsEventPairing);
          return;
        }

        if (wsEvent.type === WsEventType.DRIP_CHAMBER_DETECTION) {
          this.handleDripChamberDetection(wsEvent as WsEventDripChamberDetected);
          return;
        }

        if (wsEvent.type === WsEventType.NEW_THERAPY_JOB) {
          this.handleJobCreation();
          return;
        }

        if (wsEvent.type === WsEventType.JOB_FINALIZED) {
          this.handleJobFinalized(wsEvent as WsEventFinalized);
          return;
        }

        if (wsEvent.type === WsEventType.ABORTION_TRIGGERED) {
          this.handleJobAbortedTriggered(wsEvent as WsEventAborted);
        }

        if (wsEvent.type === WsEventType.DEVICE_ERROR_STATE) {
          this.handleDeviceErrorState(wsEvent as WsEventState);
        }

        if (wsEvent.type === WsEventType.NOTIFICATION || wsEvent.type === WsEventType.DEVICE_ERROR_STATE) {
          this.handleNotification();
        }

        if (wsEvent.type === WsEventType.HIS_UPDATE) {
          this.handleHISUpdates();
        }
      });
  }
  public destroySubscription() {
    this.completeSubscription.next();
    this.completeSubscription.complete();
  }

  private handleHISUpdates() {
    this.jobStore.load(false);
  }

  /** The reload also rebuilds notifications from the refreshed jobs, so nothing else is needed here. */
  private handleResyncRequired() {
    setTimeout(() => this.jobStore.load(false), Math.random() * RESYNC_JITTER_MAX_TIME);
  }

  private handleNotification() {
    this.notificationRefreshRequests.next();
  }

  private handleDeviceErrorState(wsEvent: WsEventState) {
    wsEvent.events?.forEach((deviceError) => {
      if (deviceError.disconnected === undefined) {
        return;
      }

      this.jobStore.applyWsPatch(
        {
          deviceDisconnected: deviceError.disconnected,
          event: { type: WsEventType.DEVICE_ERROR_STATE, timestamp: deviceError.ts }
        },
        deviceError.therapyJobId
      );
    });
  }

  private handleInfusionUpdate(wsEvent: WsEventInfusion) {
    const tj = this.jobStore.entityMap()[wsEvent.therapyJobId];

    this.handleStatusChange(tj, wsEvent);
    this.handleTherapyJobProgress(wsEvent);
  }

  private handleStatusChange(storedJob: TherapyJobV2, wsEvent: WsEventInfusion): void {
    if (storedJob?.status === wsEvent.therapyJobStatus) {
      return;
    }

    const startTime = storedJob?.status === TherapyJobStatus.PRESCRIBED ? wsEvent.ts : storedJob?.startTimeReal;

    this.jobStore.applyWsPatch(
      {
        status: wsEvent.therapyJobStatus || storedJob.status,
        startTimeReal: startTime,
        event: { type: WsEventType.JOB_UPDATE }
      },
      wsEvent.therapyJobId
    );
  }

  private handleTherapyJobProgress(wsEvent: WsEventInfusion) {
    // We delete the timestamp to prevent continuous re-rendering
    delete wsEvent.ts;

    this.updatesCollector.next({
      id: wsEvent.therapyJobId,
      therapyJobProgress: wsEvent,
      event: { type: WsEventType.JOB_UPDATE }
    });
  }

  private handleJobFinish(wsEvent: WsEventInfusion) {
    this.jobStore.applyWsPatch(
      {
        status: TherapyJobStatus.COMPLETED,
        endTime: wsEvent.ts,
        event: { type: WsEventType.JOB_FINISH, timestamp: wsEvent.ts }
      },
      wsEvent.therapyJobId
    );
  }

  private handlePairing(wsEvent: WsEventPairing) {
    // we set a new value to the pairing signal, so all the
    // components that are listening can update
    this.signalizePairing(wsEvent);

    // we prepare the patch object that will be used to update the job
    const tj = this.jobStore.entityMap()?.[wsEvent.therapyJobId];

    // we get the abort/complete time if the status is aborted
    const endTime = wsEvent.therapyJobStatus === TherapyJobStatus.COMPLETE_CONFIRMED ? wsEvent.ts : null;
    const abortTime = wsEvent.therapyJobStatus === TherapyJobStatus.ABORTED ? wsEvent.ts : null;

    const patch: Partial<TherapyJobV2> = {
      // when paired, disconnection should be recovered from event, this change breaks the playwright tests need to be reconsidered
      // deviceDisconnected: wsEvent.pairingState == PairingStatus.PAIRED || tj?.deviceDisconnected,
      monitorDeviceId: wsEvent.pairingState === PairingStatus.UN_PAIRED ? null : wsEvent.deviceId,
      pairingState: wsEvent.pairingState || tj?.pairingState,
      status: wsEvent.therapyJobStatus || tj?.status,
      abortionTriggered: wsEvent.therapyJobStatus === TherapyJobStatus.ABORTED ? false : tj?.abortionTriggered,
      endTime: endTime || tj?.endTime,
      abortTime: abortTime || tj?.abortTime,
      messageKey: (wsEvent?.messageKey as WsEventMessageKey) || tj?.messageKey,
      dripChamberDetected: wsEvent.dripChamberDetected ?? tj?.dripChamberDetected,
      event: { type: WsEventType.PAIRING, timestamp: wsEvent.ts }
    };

    // We check if the pairing state is unpaired and if the neverPaired property should be set to false
    // this will edit the patch object
    this.possibleMarkAsOncePaired(wsEvent, patch);

    this.jobStore.applyWsPatch(patch, wsEvent.therapyJobId);
  }

  private handleDripChamberDetection(wsEvent: WsEventDripChamberDetected) {
    this.jobStore.applyWsPatch(
      {
        dripChamberDetected: wsEvent.detected,
        event: { type: WsEventType.JOB_STATE, timestamp: wsEvent.ts }
      },
      wsEvent.therapyJobId
    );
  }

  private handleJobCreation() {
    this.jobStore.load(false);
  }

  private handleJobAbortedTriggered(wsEvent: WsEventAborted) {
    this.jobStore.applyWsPatch(
      {
        abortionTriggered: true,
        event: { type: WsEventType.ABORTION_TRIGGERED, timestamp: wsEvent.ts }
      },
      wsEvent.therapyJobId
    );
  }

  private signalizePairing(wsEvent: WsEventPairing) {
    // Clear any existing timer to avoid multiple resets
    clearTimeout(this.timer);

    // Set the new pairing event
    this.pairing.set(wsEvent);

    // Reset the pairing event after 1 second
    this.timer = setTimeout(() => {
      this.pairing.set(null);
    }, PAIRING_SIGNAL_DURATION);
  }

  private possibleMarkAsOncePaired(wsEvent: WsEventPairing, patch: Partial<TherapyJobV2>) {
    // if there is an event about paired we should set the neverPaired property to false
    const shouldEditNeverPairedProperty = wsEvent.pairingState === PairingStatus.PAIRED;

    if (shouldEditNeverPairedProperty) {
      Object.assign(patch, { neverPaired: false });
    }
  }

  private handleJobFinalized(wsEvent: WsEventFinalized) {
    this.jobStore.applyWsPatch(
      {
        status: TherapyJobStatus.IN_HISTORY,
        endTime: wsEvent.ts
      },
      wsEvent.therapyJobId
    );
  }

  private showInfoMessage(messageKey: string, id?: string) {
    this.toastService.show({
      message: this.translateService.instant(messageKey, { id }),
      level: NotificationLevel.INFO,
      type: ToastType.DEFAULT,
      enableCloseButton: true,
      delay: 20_000
    });
  }
}
